// src/components/oracle/OracleCardCollection.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { oracleDeck } from '../../data/oracle/oracleDeckData';
import { useGlow } from '../../hooks/useGlow';
import { useLocation } from 'react-router-dom';

const OracleCardCollection = ({ cardIds, lang = 'es' }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedCards = cardIds
    .map(id => oracleDeck.find(c => c.id == id) || oracleDeck.find(c => c.idName?.trim()?.toString()?.toUpperCase() == id?.trim()?.toUpperCase()))
    .filter(Boolean);

  const collectionTitle = selectedCards.length > 0 
    ? selectedCards[0][lang]?.category 
    : '';

  const titleStyle = useGlow({ 
    gradient: 'linear-gradient(to bottom, #e0e0e0ff, #9b977aff, #9e823cff, #776f5cff)', 
    intensityMin: 0.2, 
    intensityMax: 0.4 
  });

  const cardTitleStyle = useGlow({
    gradient: 'linear-gradient(to bottom, #e0e0e0ff, #9b977aff, #9e823cff, #e0e0e0ff)',
    intensityMin: 0.3,
    intensityMax: 0.6,
    intervalDuration: 2500
  });

  const cardTitleStyle2 = useGlow({
    gradient: 'linear-gradient(to bottom, #e0e0e0ff, rgb(155, 142, 122), rgb(158, 99, 60), #e0e0e0ff)',
    intensityMin: 0.3,
    intensityMax: 0.6,
    intervalDuration: 2500
  });

  const handleCardClick = (id) => {
    const path = lang === 'es' ? `/oraculo/${id}` : `/oracle/${id}`;
    navigate(path);
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-4 md:p-8 text-white animate-fade-in">
      {collectionTitle && (
        <h2 style={titleStyle} className="text-3xl md:text-5xl font-bold mb-8 md:mb-16 text-center uppercase tracking-tighter">
          {collectionTitle}
        </h2>
      )}

      <div className="flex flex-col gap-4 md:gap-8">
        {selectedCards.map((card) => {
          const content = card[lang];
          return (
            <div 
              key={card.id}
              onClick={() => handleCardClick(card.id)}
              className="group relative flex flex-row items-center gap-4 md:gap-8 bg-black/20 md:bg-gray-900/50 border-b border-white/5 md:border md:border-purple-500/30 rounded-2xl p-3 md:p-6 transition-all duration-500 hover:md:bg-gray-800/70 hover:md:shadow-[0_0_30px_rgba(168,85,247,0.15)] cursor-pointer overflow-hidden"
            >
              <span className="hidden md:block absolute -right-4 -bottom-4 text-8xl font-bold text-white/5 italic pointer-events-none group-hover:text-purple-500/10 transition-colors">
                #{card.id}
              </span>

              <div className="flex-shrink-0 w-20 sm:w-28 md:w-44 transform group-hover:scale-105 transition-transform duration-500">
                <img src={card.image} alt={content.name} className="w-full h-auto object-contain rounded-lg border border-sky-400/30 shadow-lg" />
              </div>

              <div className="flex-grow text-left">
                <span className="md:hidden text-[10px] text-purple-400/70 font-bold">#{card.id}</span>
                
                <h3 style={cardTitleStyle} className="text-lg sm:text-2xl md:text-3xl font-bold leading-tight">
                  {content.name}
                </h3>
                {content.name2 && (
                  <h3 style={cardTitleStyle2} className="text-lg sm:text-2xl md:text-3xl font-bold leading-tight">
                    {content.name2}
                  </h3>
                )}
                {content.name3 && (
                  <h3 style={cardTitleStyle2} className="text-lg sm:text-2xl md:text-3xl font-bold leading-tight">
                    {content.name3}
                  </h3>
                )}
                
                {/* Keywords: Ahora visibles en móvil con ajustes de tamaño */}
                <div className="flex mt-2 md:mt-4 flex-wrap gap-1 md:gap-2">
                  {content.keywords && (Array.isArray(content.keywords) ? content.keywords : content.keywords.split(','))
                    .slice(0, 3)
                    .map((kw, i) => (
                      <span key={i} className="text-[8px] md:text-[10px] uppercase border border-white/20 px-1.5 py-0.5 rounded text-gray-400 whitespace-nowrap">
                        {kw.trim()}
                      </span>
                    ))}
                </div>
              </div>

              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-full border border-purple-500/20 bg-purple-500/5 md:bg-purple-500/10 group-hover:bg-purple-500/30 transition-all">
                  <span className="text-lg md:text-2xl text-purple-400">→</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OracleCardCollection;